import { useDispatch, useSelector } from "react-redux";
import { ApiCallStatusV2, CANCELLED_REASONS } from "src/common/ApiCall";
import { RootState } from "src/redux/store";
import React, { useEffect, useState } from "react";
import { InputsForCurrentAccessControlsBasedOnInformationAsset } from "../model/requestParams";
import axios, { CancelTokenSource } from "axios";
import { SystemResourceType } from "../model/systemResourceTypes";
import { AccessVisibilityActions } from "../model/model";
import { AuthZTable } from "src/components/authZTable";
import "../index.scss";
import {
  CurrentAccessControlBasedOnInformationAsset,
  CurrentAccessControlsBasedOnInformationAssets,
} from "../model/currentAccessControlsBasedOnInformationAsset";
import { useLocation } from "react-router-dom";
import {
  ObtainedViaForSystemUserGroup,
  ObtainedViaForUserGroup,
  ObtainedViaType,
} from "../model/obtainedViaForAccessControl";

export function AccessVisibilityBasedOnInformationAssets(props: {
  userEmail: Nullable<string>;
  filteredSystemResourceType: SystemResourceType;
  userGroup: Nullable<{
    id: string;
    type: string;
  }>;
  toShowFor: { user: boolean; userGroup: boolean };
  reduceTableHeight: string | null;
  isActive: boolean | null
}) {
  const dispatch = useDispatch();
  const accessVisibility = useSelector((state: RootState) => state.accessVisibility);
  const { pathname } = useLocation();
  const {filteredSystemResourceType} = props;
  const currentAccessVisibilityBasedOnInformationAssetApiCallStatus = new ApiCallStatusV2(
    accessVisibility.currentAccessControlsBasedOnInformationAssets.progress,
    accessVisibility.currentAccessControlsBasedOnInformationAssets.error,
  );

  const currentAccessControlsBasedOnInformationAssets =
    new CurrentAccessControlsBasedOnInformationAssets(
      accessVisibility.currentAccessControlsBasedOnInformationAssets.value.currentAccessControls,
    );
  const totalCurrentAccessControlsBasedOnSystemResource =
    accessVisibility.currentAccessControlsBasedOnInformationAssets.value.totalCount;

  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filters, setFilters] = useState<{
    obtainedVia: string | null;
    resource: string | null;
    resourceGroup: string | null;
    resourceDomain: string | null;
    resourceClassificationLevel: string | null;
  }>({
    obtainedVia: null,
    resource: null,
    resourceGroup: null,
    resourceDomain: null,
    resourceClassificationLevel: null
  });
  const [requestPayload, setRequestPayload] =
    useState<InputsForCurrentAccessControlsBasedOnInformationAsset | null>(null);
  const [axiosCancelRequestsToken, setAxiosCancelRequestsToken] =
    useState<CancelTokenSource | null>(null);

  let columns = [
    {
      title: "Information assets",
      dataIndex: "informationAssets",
      key: "informationAssets",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        return <div className={"resource-cell"}>{record.resource.name}</div>;
      },
      onCell: (record: CurrentAccessControlBasedOnInformationAsset) => ({
        rowSpan: record.rowSpan.resourceSpan,
      }),
      sortEnabled: false,
      width: "15%",
    },
    {
      title: "Group",
      dataIndex: "group",
      key: "group",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        return <div className={"resource-group-cell"}>{record.resource.group}</div>;
      },
      onCell: (record: CurrentAccessControlBasedOnInformationAsset) => ({
        rowSpan: record.rowSpan.resourceSpan,
      }),
      sortEnabled: false,
      width: "15%",
    },
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        return <div className={"resource-domain-cell"}>{record.resource.domain}</div>;
      },
      onCell: (record: CurrentAccessControlBasedOnInformationAsset) => ({
        rowSpan: record.rowSpan.resourceSpan,
      }),
      sortEnabled: false,
      width: "15%",
    },
    {
      title: "Classification level",
      dataIndex: "classificationLevel",
      key: "classificationLevel",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        return record.renderAttribute("classification");
      },
      onCell: (record: CurrentAccessControlBasedOnInformationAsset) => ({
        rowSpan: record.rowSpan.resourceSpan,
      }),
      sortEnabled: false,
      width: "20%",
    },
    {
      title: "Obtained via",
      dataIndex: "obtainedVia",
      key: "obtainedVia",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        return record.obtainedVia.render(filteredSystemResourceType.id, "information-assets");
      },
      onCell: (record: CurrentAccessControlBasedOnInformationAsset) => ({
        rowSpan: record.rowSpan.obtainedViaSpan,
      }),
      sortEnabled: false,
      width: "20%",
    },
    {
      title: `Count of ${filteredSystemResourceType.name}s`,
      dataIndex: "obtainedVia",
      key: "obtainedVia",
      render: (_text, record: CurrentAccessControlBasedOnInformationAsset) => {
        let updatedUrl = pathname.slice(0, pathname.indexOf("information-assets")) +
          `system-resources?resourceId=${record.resourceId()}`;
        const obtainedViaType = record.obtainedVia.type;
        if (obtainedViaType === ObtainedViaType.UserGroup) {
          updatedUrl += "&userGroupType=" + (record.obtainedVia as ObtainedViaForUserGroup).typeId;
          updatedUrl += "&userGroupId=" + (record.obtainedVia as ObtainedViaForUserGroup).id;
        } else if (obtainedViaType === ObtainedViaType.SystemUserGroup) {
          updatedUrl += "&systemUserGroupId=" + (record.obtainedVia as ObtainedViaForSystemUserGroup).id;
        } else {
          updatedUrl += "&obtainedViaType=" + obtainedViaType;
        }
        return record.countOfSystemResources(updatedUrl, filteredSystemResourceType.name);
      },
      sortEnabled: false,
      searchEnabled: false,
      width: "20%",
    },
  ];

  function removeColumn(columnName: string) {
    columns = columns.filter((column) => column.title !== columnName);
  }

  if (props.toShowFor.userGroup || props.filteredSystemResourceType.isScopeApplicable()) {
    removeColumn("Obtained via");
  }

  function onCustomFilterAndCustomSortChange(filters: {
    obtainedVia: Array<string> | undefined;
    informationAssets: Array<string> | undefined;
    group: Array<string> | undefined;
    domain: Array<string> | undefined;
    classificationLevel: Array<string> | undefined;
  }) {
    setFilters({
      obtainedVia: filters.obtainedVia?.[0] ?? null,
      resource: filters.informationAssets?.[0] ?? null,
      resourceGroup: filters.group?.[0] ?? null,
      resourceDomain: filters.domain?.[0] ?? null,
      resourceClassificationLevel: filters.classificationLevel?.[0] ?? null,
    });
  }

  useEffect(() => {
    setRequestPayload(
      new InputsForCurrentAccessControlsBasedOnInformationAsset({
        systemId: props.filteredSystemResourceType.systemId,
        systemResourceType: props.filteredSystemResourceType.name,
        principalId: props.userEmail,
        obtainedVia: filters.obtainedVia,
        resource: filters.resource,
        userGroupId: props.userGroup?.id ?? null,
        userGroupType: props.userGroup?.type ?? null,
        resourceGroup: filters.resourceGroup,
        resourceDomain: filters.resourceDomain,
        resourceClassificationLevel: filters.resourceClassificationLevel,
        pageNumber: pageNumber,
        pageSize: pageSize,
        toShowFor: props.toShowFor,
      }),
    );
  }, [
    JSON.stringify(props.filteredSystemResourceType.identifier()),
    pageNumber,
    pageSize,
    JSON.stringify(filters),
  ]);

  useEffect(() => {
    if (requestPayload !== null && props.isActive) {
      const axiosCancelToken = axios.CancelToken.source();
      dispatch(
        AccessVisibilityActions.getCurrentAccessControlsBasedOnInformationAssets({
          requestBody: requestPayload.requestParams(),
          axiosConfig: {
            cancelToken: axiosCancelToken.token,
          },
        }),
      );
      if (axiosCancelRequestsToken !== null) {
        axiosCancelRequestsToken.cancel(CANCELLED_REASONS.CalledApiAgain);
      }
      setAxiosCancelRequestsToken(axiosCancelToken);
    }
  }, [JSON.stringify(requestPayload?.requestParams())]);

  return (
    <div className="current-access-controls-content">
      {currentAccessVisibilityBasedOnInformationAssetApiCallStatus.isFailed ? (
        <div className="error-text">Error loading details</div>
      ) : (
        <AuthZTable
          columns={columns}
          dataSource={currentAccessControlsBasedOnInformationAssets.currentAccessControls}
          isFilteredOrSortedInBackend={true}
          loading={currentAccessVisibilityBasedOnInformationAssetApiCallStatus.isLoading}
          scroll={{ y: `calc(100vh - ${props.reduceTableHeight ?? "500px"})` }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            pageSize: pageSize,
            pageSizeOptions: [10, 25, 50],
            showTotal: (total: number, range: Array<number>) =>
              `${range[0]}-${range[1]} of ${total} items`,
            current: pageNumber,
            total: totalCurrentAccessControlsBasedOnSystemResource,
            onChange: (page: number, pageSize: number) => {
              setPageSize(pageSize);
              setPageNumber(page);
            },
          }}
          onCustomFilterAndCustomSortChange={onCustomFilterAndCustomSortChange}
        />
      )}
    </div>
  );
}
